import { useEffect, useRef, useState } from "react";
import { Activity, CheckCircle2, GitCommit, FileCode2, Clock } from "lucide-react";
import { createProject, listProjects } from "../lib/api";

/**
 * Full-screen "Analyzing your repo…" overlay shown after a user picks a repo.
 * Runs the createProject API call AND a 2.6s minimum animation in parallel,
 * then calls onComplete(project).
 */
export default function ProcessingScreen({ fullName, onComplete, onError }) {
  const [step, setStep] = useState(0);
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [slow, setSlow] = useState(false);

  const projectRef = useRef(null);
  const minDoneRef = useRef(false);
  const finishedRef = useRef(false);
  const startedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  // keep latest callbacks
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const tryFinish = () => {
      if (finishedRef.current) return;
      if (!minDoneRef.current || !projectRef.current) return;
      finishedRef.current = true;
      setStep(5);
      setTimeout(() => onCompleteRef.current?.(projectRef.current), 350);
    };

    // animate steps
    const timers = [];
    [1, 2, 3, 4].forEach((s, i) => {
      timers.push(setTimeout(() => setStep(s), 600 + i * 700));
    });
    timers.push(
      setTimeout(() => {
        minDoneRef.current = true;
        tryFinish();
      }, 2600)
    );

    // API
    createProject(fullName)
      .then((p) => {
        projectRef.current = p;
        setProject(p);
        tryFinish();
      })
      .catch(async (e) => {
        if (finishedRef.current) return;
        // 409 Conflict — already added. Look it up and navigate to it.
        if (e?.response?.status === 409) {
          try {
            const list = await listProjects();
            const existing = list.find((x) => x.full_name === fullName);
            if (existing) {
              projectRef.current = existing;
              setProject(existing);
              tryFinish();
              return;
            }
          } catch {
            /* fall through */
          }
        }
        finishedRef.current = true;
        const msg = e?.response?.data?.detail || "Failed to add project";
        setError(msg);
        onErrorRef.current?.(msg);
      });

    // safety net — if anything stalls, surface an error after 90s
    const safety = setTimeout(() => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      const msg = "This took longer than expected. Please retry.";
      setError(msg);
      onErrorRef.current?.(msg);
    }, 90000);
    timers.push(safety);

    // reassurance — show a "still working" line if we're past 25s
    timers.push(
      setTimeout(() => {
        if (finishedRef.current) return;
        setStep((s) => Math.max(s, 4));
        setSlow(true);
      }, 25000)
    );

    return () => timers.forEach(clearTimeout);
  }, [fullName]);

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="processing-screen">
      <div className="absolute inset-0 pointer-events-none opacity-[0.35]" style={{
        backgroundImage:
          "url('https://static.prod-images.emergentagent.com/jobs/d51a684d-6a21-4b39-8b43-c02965a25ab0/images/2f2e3d13da85b38ad9d2ad0dbbc6e45bf434e189b48350d06f58b02836e01f45.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }} />
      <div className="relative flex-1 flex flex-col items-center justify-center px-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
            <Activity size={14} className="text-white" strokeWidth={2.4} />
          </div>
          <span className="text-[15px] font-semibold tracking-tight text-[#111827]">Trace</span>
        </div>

        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#111827]">
          Analyzing your repo…
        </h1>
        <p className="mt-2 text-sm text-[#6b7280] font-mono">{fullName}</p>

        <ul className="mt-10 w-full max-w-[420px] space-y-3 text-[14px]">
          <Line revealed={step >= 1} icon={<GitCommit size={14} />}>
            <Counted active={step >= 1} target={getCounts(project).commits} label="commits" />
          </Line>
          <Line revealed={step >= 2} icon={<FileCode2 size={14} />}>
            <Counted active={step >= 2} target={getCounts(project).files} label="files touched" />
          </Line>
          <Line revealed={step >= 3} icon={<Clock size={14} />}>
            <Counted active={step >= 3} target={getCounts(project).sessions} label="active work sessions" />
          </Line>
          <Line revealed={step >= 4} icon={<Activity size={14} />} pulse={step === 4}>
            {slow ? "Big repo — still working, hang tight…" : "Building your project memory…"}
          </Line>
        </ul>

        <div className="mt-10 w-full max-w-[420px]">
          <div className="h-1 rounded-full bg-[#f1f3f5] overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-700 ease-out"
              style={{ width: `${Math.min(100, step * 22)}%` }}
            />
          </div>
        </div>

        {error && (
          <div className="mt-8 text-sm text-rose-600" data-testid="processing-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function Line({ revealed, icon, children, pulse = false }) {
  return (
    <li
      className={`flex items-center gap-3 transition-all duration-500 ${
        revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1.5"
      }`}
    >
      <span
        className={`w-7 h-7 rounded-full flex items-center justify-center border ${
          revealed
            ? "bg-white border-[#e5e7eb] text-emerald-500"
            : "bg-[#f7f8fa] border-[#e5e7eb] text-[#9ca3af]"
        } ${pulse ? "trace-pulse" : ""}`}
      >
        {revealed ? <CheckCircle2 size={14} /> : icon}
      </span>
      <span className="text-[#111827]">{children}</span>
    </li>
  );
}

function Counted({ active, target, label }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active || !target) {
      setN(target || 0);
      return;
    }
    const dur = 700;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.round(target * p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target]);
  return (
    <span>
      Found <strong className="text-[#111827] tabular-nums">{n}</strong>{" "}
      <span className="text-[#6b7280]">{label}</span>
    </span>
  );
}

function getCounts(project) {
  if (!project) return { commits: 0, files: 0, sessions: 0 };
  const commits = Math.max(project.files_count, project.sessions_count * 3);
  return {
    commits: commits || 0,
    files: project.files_count || 0,
    sessions: project.sessions_count || 0,
  };
}
